import React, { useEffect, useRef, useState } from 'react';
import { acceptCall, cancelCall, declineCall, getBlockedSlots } from '../../../../api/v2/calls';
import { trackPhoneCallClick } from '../../../../api/activities';
import { failToast } from '../../../ui_kit/utilities/fail_toast';
import AvailabilityPicker from '../../../phone_calls/availability_picker';
import CallEventCard from '.';
import { buildCallEventCardProps } from './build_props';

const ACTIVE_STATUSES = ['pending', 'accepted', 'in_progress'];

function shouldFetchBlockedSlots(call, isMentor) {
  return Boolean(call && call.scheduled && call.status === 'pending' && (!isMentor || isCounterable(call, isMentor)));
}

function isCounterable(call, isMentor) {
  if (!call || !isMentor) return false;
  return call.status === 'pending' && call.actionable && call.scheduled && call.countered_by_type !== 'PatientPartner';
}

export default function ConnectedCallEventCard({
  call: propCall,
  conversationUuid,
  isMentor,
  viewerTimezone,
  viewerPhone,
  otherUserFirstName,
  otherUserName,
  otherUserPhoto,
  otherUserTimezone,
  onCallActed,
  onScheduleCall,
}) {
  const [call, setCall] = useState(propCall);
  const [isCanceling, setIsCanceling] = useState(false);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [showCounterPicker, setShowCounterPicker] = useState(false);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  useEffect(() => {
    setCall(propCall);
  }, [propCall?.uuid, propCall?.status, propCall?.counter_count, propCall?.countered_by_type, propCall?.scheduled_at]);

  useEffect(() => {
    if (!shouldFetchBlockedSlots(call, isMentor)) {
      setBlockedSlots([]);
      return;
    }

    getBlockedSlots(conversationUuid)
      .then((data) => {
        if (isMounted.current) setBlockedSlots(data.blocked_slots || []);
      })
      .catch(() => {
        if (isMounted.current) setBlockedSlots([]);
      });
  }, [conversationUuid, call?.uuid, call?.scheduled, call?.status, isMentor]);

  if (!call) return null;

  const isActive = ACTIVE_STATUSES.includes(call.status);
  const counterTimezone = isMentor ? viewerTimezone : otherUserTimezone;

  const trackCardClick = (name) => {
    trackPhoneCallClick(name, { conversation_id: conversationUuid, call_id: call.uuid });
  };

  const handleAcceptSlot = (slot) => {
    trackCardClick('call_event_card_accept_time');
    acceptCall({ callId: call.uuid, scheduledAt: slot.start_at })
      .then((updatedCall) => {
        if (isMounted.current) setCall(updatedCall);
        onCallActed?.();
      })
      .catch((err) => failToast(err.message || 'Could not accept. Please try again.'));
  };

  const handleAccept = () => {
    trackCardClick('call_event_card_accept_call');
    acceptCall({ callId: call.uuid })
      .then((updatedCall) => {
        if (isMounted.current) setCall(updatedCall);
        onCallActed?.();
      })
      .catch((err) => failToast(err.message || 'Could not accept. Please try again.'));
  };

  const handleCancel = () => {
    setIsCanceling(true);

    const isMentorDecliningIncoming = isMentor && call.status === 'pending' && !call.countered_at;
    trackCardClick(isMentorDecliningIncoming ? 'call_event_card_decline_call' : 'call_event_card_cancel_call');

    const apiCall = isMentorDecliningIncoming ? declineCall : cancelCall;
    apiCall({ callId: call.uuid })
      .then((updatedCall) => {
        if (isMounted.current) setCall(updatedCall);
        onCallActed?.();
      })
      .catch((err) =>
        failToast(
          (err.message || 'Could not cancel. Please try again.') +
            ' Please try again or reach out to support@patientpartner.com if this continues.'
        )
      )
      .finally(() => {
        if (isMounted.current) setIsCanceling(false);
      });
  };

  const handleCounter = () => {
    trackCardClick('call_event_card_propose_new_times');
    setShowCounterPicker(true);
  };

  const handleScheduleClick = () => {
    trackCardClick('call_event_card_schedule_a_call');
    onScheduleCall();
  };

  const handleCounterCreated = (updatedCall) => {
    if (isMounted.current) {
      setShowCounterPicker(false);
      if (updatedCall) setCall(updatedCall);
    }
    onCallActed?.();
  };

  const props = buildCallEventCardProps({
    call,
    isMentor,
    viewerTimezone,
    viewerPhone,
    otherUserFirstName,
    blockedSlots,
    onAcceptSlot: isActive ? handleAcceptSlot : undefined,
    onAccept: isActive ? handleAccept : undefined,
    onCancel: isActive ? handleCancel : undefined,
    onCounter: isActive ? handleCounter : undefined,
    onSchedule: typeof onScheduleCall === 'function' ? handleScheduleClick : undefined,
  });

  if (!props) return null;

  return (
    <>
      <CallEventCard {...props} isCanceling={isCanceling} />
      {showCounterPicker && (
        <AvailabilityPicker
          conversationId={conversationUuid}
          existingCallId={call.uuid}
          mentorTimezone={counterTimezone}
          viewerTimezone={viewerTimezone}
          mentorFirstName={otherUserFirstName}
          otherUserName={otherUserName}
          otherUserPhoto={otherUserPhoto}
          blockedSlots={blockedSlots}
          onClose={() => setShowCounterPicker(false)}
          onCallCreated={handleCounterCreated}
        />
      )}
    </>
  );
}
